import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { EyeIcon, EyeOff } from 'lucide-react';

import { Button } from '@/slw/shared/ui/button';
import FormProvider, { Input } from '@/slw/shared/ui/form';
import { Label } from '@/slw/shared/ui/label';
import { PasswordStrengthBar } from '@/slw/shared/ui/password-strength';
import { PasswordRequirements } from '@/slw/shared/ui/password-requirements';
import { updatePasswordSchema } from '@/slw/entities/auth/auth.schema';
import { UpdatePasswordFormValues } from '@/slw/entities/auth/auth.type';
import { cn } from '@/slw/shared/lib/utils';

export function UpdatePasswordStep({
  email,
  onSubmit,
  loading,
}: {
  email: string;
  onSubmit: (values: UpdatePasswordFormValues) => void;
  loading: boolean;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const methods = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: '',
      confirm: '',
    },
  });
  const password = methods.watch('password');
  const confirm = methods.watch('confirm');
  const passwordsMatch = password === confirm;

  const errorConfirm = methods.formState.errors?.confirm;

  return (
    <FormProvider {...methods} onSubmit={(v) => onSubmit(v)} className='w-full flex flex-col justify-center'>
      <p className='font-normal text-sm mb-[2rem]'>Your email address: {email}</p>
      
      {/* Password Field */}
      <div className='flex flex-col gap-2 mb-6'>
        <Label htmlFor='password' className='whitespace-nowrap font-normal text-sm'>
          New Password<span className='text-[#dc2626]'>*</span>
        </Label>
        <div className='relative'>
          <Input
            direction='column'
            id='password'
            name='password'
            type={showPassword ? 'text' : 'password'}
            placeholder='Enter your new password'
            helperText='Choose a strong password that meets all requirements below.'
          />
          <button
            type='button'
            className='absolute right-4 top-1/2 -translate-y-[calc(50%+12px)]'
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeIcon className='w-5 h-5 text-default-400' /> : <EyeOff className='w-5 h-5 text-default-400' />}
          </button>
        </div>
        {password && <PasswordStrengthBar password={password} />}
        <PasswordRequirements password={password} />
      </div>

      {/* Confirm Password Field */}
      <div className='flex flex-col gap-2 mb-6'>
        <Label htmlFor='confirm' className='whitespace-nowrap font-normal text-sm'>
          Confirm Password<span className='text-[#dc2626]'>*</span>
        </Label>
        <div className='relative'>
          <Input
            direction='column'
            id='confirm'
            name='confirm'
            type={showConfirm ? 'text' : 'password'}
            placeholder='Confirm your new password'
            helperText={confirm && !passwordsMatch ? 'Passwords do not match' : ''}
          />
          <button
            type='button'
            className={cn(
              'absolute right-4 top-1/2 -translate-y-1/2',
              errorConfirm && '-translate-y-[calc(50%_+_0.7rem)]',
            )}
            onClick={() => setShowConfirm((v) => !v)}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
          >
            {showConfirm ? (
              <EyeIcon className='w-5 h-5 text-default-400' />
            ) : (
              <EyeOff className='w-5 h-5 text-default-400' />
            )}
          </button>
        </div>
        {confirm && passwordsMatch && (
          <div className='text-xs text-green-600 flex items-center gap-1'>
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Passwords match
          </div>
        )}
      </div>

      <Button type='submit' disabled={loading || !passwordsMatch || !password} className='w-full mt-8'>
        {loading ? 'Loading...' : 'Update Password'}
      </Button>
    </FormProvider>
  );
}
